import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory, Redirect } from "react-router-dom";
import { Box, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Formik } from "formik";
import * as Yup from "yup";
import Navbar from "components/Navbar";
import DiagnosticsBar from "components/Masterbar/DiagnosticsBar";
import LightLayout from "components/Shared/LightLayout";
import { Input, Select } from "components/Shared/mui-formik-inputs";
import FormCancelSaveButton from "components/Shared/FormCancelSaveButtons";
import {
  getDiagnosticsSettings,
  selectDiagnosticsLoading,
  selectDiagnosticsSettings,
  updateMobileDiagnosticsSettings,
} from "redux/slices/diagnosticsSlice";
import { selectUser } from "redux/slices/userSlice";
import { PATHS } from "util/appConstants";

const useStyles = makeStyles({
  heading: {
    font: "normal normal normal 28px/40px Questrial",
    color: "#121212",
    marginBottom: 24,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: 24,
  },
});

const isDiagnosticsVisible = (user) =>
  user?.isSuperAdmin && import.meta.env.VITE_SHOW_SUPPORT === "true";

const DiagnosticsMobileSettings = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector(selectUser);
  const loading = useSelector(selectDiagnosticsLoading);
  const settings = useSelector(selectDiagnosticsSettings);

  useEffect(() => {
    if (!isDiagnosticsVisible(user)) {
      return;
    }
    dispatch(getDiagnosticsSettings());
  }, [dispatch, user]);

  const initialMobileSettings = useMemo(() => {
    const mobile = settings?.settings?.mobile || {};
    const forceUpdate = mobile.force_update_after || {};
    return {
      force_update_value:
        forceUpdate.value != null && typeof forceUpdate === "object"
          ? forceUpdate.value
          : 1,
      force_update_unit:
        forceUpdate.unit === "hours" || forceUpdate.unit === "days"
          ? forceUpdate.unit
          : "days",
      min_version: mobile.min_version || "",
    };
  }, [settings]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        force_update_value: Yup.number()
          .integer()
          .min(1, t("Required"))
          .required(t("Required")),
        force_update_unit: Yup.string()
          .oneOf(["hours", "days"])
          .required(t("Required")),
        min_version: Yup.string().nullable(),
      }),
    [t],
  );

  if (!isDiagnosticsVisible(user)) {
    return <Redirect to={PATHS.admin.supplierGroups} />;
  }

  return (
    <>
      <Navbar />
      <DiagnosticsBar />
      <LightLayout doublebar loading={loading && !settings}>
        <Formik
          enableReinitialize
          initialValues={initialMobileSettings}
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            try {
              await dispatch(
                updateMobileDiagnosticsSettings({
                  force_update_after: {
                    value: Number(values.force_update_value),
                    unit: values.force_update_unit,
                  },
                  min_version: values.min_version?.trim() || null,
                }),
              );
              history.push(PATHS.diagnostics.mobile);
            } catch (error) {
              // error toast is handled in the slice
            }
          }}
        >
          {({ values, errors, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <Box className={classes.actions}>
                <FormCancelSaveButton
                  disabled={isSubmitting}
                  onCancel={() => history.push(PATHS.diagnostics.mobile)}
                  onSave={handleSubmit}
                />
              </Box>
              <Typography className={classes.heading}>
                {t("Force Update Settings")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Input
                    name="force_update_value"
                    label={t("Force update after")}
                    type="number"
                    value={values.force_update_value}
                    errors={errors}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputProps={{ min: 1, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Select
                    name="force_update_unit"
                    label={t("Unit")}
                    value={values.force_update_unit}
                    errors={errors}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    options={[
                      { value: "hours", label: t("Hours") },
                      { value: "days", label: t("Days") },
                    ]}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Input
                    name="min_version"
                    label={t("Min version")}
                    value={values.min_version}
                    errors={errors}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    help={t("Optional. Leave empty for no minimum version.")}
                  />
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </LightLayout>
    </>
  );
};

export default DiagnosticsMobileSettings;
